/**
 * Fichier : chat_widget/src/widget/hooks/useChatSession.ts
 *
 * Principe :
 * Ce hook gère l'identité de session du widget de chat côté client.
 *
 * Il est responsable de :
 * - déterminer l'`effectiveUserId` (fallback "anon" si non fourni)
 * - générer un `sessionId` unique pour la page
 * - persister le `sessionId` dans le localStorage (scopé par userId)
 * - permettre un rafraîchissement manuel de session via `refreshSession()`
 *   (avec nettoyage côté API si un userId est présent)
 *
 * Objectif architectural :
 * Séparer totalement la gestion d'identité/session du reste de la logique
 * conversationnelle afin de garder `useChatPanel` focalisé sur l'orchestration.
 */

import { useEffect, useMemo, useState, useCallback } from "react";
import { API_CHAT_URL } from "../../config";

function generateSessionId() {
  try {
    return typeof window.crypto !== "undefined" && "randomUUID" in window.crypto
      ? (window.crypto as any).randomUUID()
      : `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
  } catch {
    return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
  }
}

const VCW_PAGE_SESSION_ID = generateSessionId();

export function useChatSession({ userId }: { userId?: string }) {
  const effectiveUserId = useMemo(() => userId ?? "", [userId]);
  const storageKey = useMemo(
    () => `vcw_session_${effectiveUserId || "anon"}`,
    [effectiveUserId],
  );

  // Utilisation d'un état interne pour permettre le rafraîchissement sans recharger la page
  const [currentSessionId, setCurrentSessionId] = useState<string>(() => {
    try {
      return window.localStorage.getItem(storageKey) ?? VCW_PAGE_SESSION_ID;
    } catch {
      return VCW_PAGE_SESSION_ID;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, currentSessionId);
    } catch {
      // ignore
    }
  }, [storageKey, currentSessionId]);

  const refreshSession = useCallback(async () => {
    if (userId) {
      console.log(currentSessionId);
      await fetch(`${API_CHAT_URL}/chat/session/clear/${currentSessionId}`, {
        headers: {
          "x-user-id": userId,
        },
      });
    }
    const newId = generateSessionId();
    setCurrentSessionId(newId);

    return newId;
  }, [userId, currentSessionId]);

  return {
    effectiveUserId,
    sessionId: currentSessionId,
    refreshSession,
  };
}
